import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import { Artist, FilterMode } from '../../../models/artist.model';
import { ArtistsService, ArtistRelease } from '../../../services/artists.service';
import { AuthService } from '../../../services/auth.service';
import { SeoService } from '../../../services/seo.service';
import { ArtistSuggestionService } from '../../../services/artist-suggestion.service';
import { SuggestArtistFlowService } from '../../../services/suggest-artist-flow.service';
import { LayoutService } from '../../../services/layout.service';
import { ArtistListingsUiService } from '../../../services/artist-listings-ui.service';
import { ArtistProfilePreviewDrawerComponent } from '../../../components/artist-profile-preview-drawer/artist-profile-preview-drawer.component';
import { formatNumber, formatLocalDate, DEFAULT_ARTIST_IMAGE } from '../../../utils/formatters';
import { formatScoreChange, scoreChangeClass } from '../../../utils/hypetrack-score';
import { filter } from 'rxjs/operators';
import { Subscription, firstValueFrom } from 'rxjs';
import { OverviewService } from '../../../services/overview.service';
import { DashboardTickerService } from '../../../services/dashboard-ticker.service';
import { DashboardTickerComponent } from '../../../components/dashboard-ticker/dashboard-ticker.component';
import { ViewsService } from '../../../services/views.service';
import { SavedView, SharedView } from '../../../models/saved-view.model';
import { SignInModalComponent } from '../../../components/sign-in-modal/sign-in-modal.component';
import { SaveViewPayload, TablePeriodOption, suggestViewName } from '../../../components/table-customizer/table-customizer.component';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ArtistDatePreset,
  ArtistGridUrlState,
  decodeArtistGridState,
  encodeArtistGridState,
  isArtistGridStateEqual,
  parseLocalDate,
  thisMonthRange,
  timeframeForRange,
  VIEW_URL_PARAM,
  viewUrlSlug,
  parseViewUrlParam,
} from './artist-rankings-url.util';

export type ArtistSortOption =
  | 'rank'
  | 'globalRank'
  | 'hypeScore'
  | 'name'
  | 'spotify'
  | 'youtube'
  | 'instagram'
  | 'tiktok'
  | 'totalSongs'
  | 'activeSince'
  | 'avgTrackLength'
  | 'chartedSongs';

/** One rankings list row + precomputed cell strings (avoids per-CD method calls in large @for tables). */
export interface ArtistRankingsTableRow {
  artist: Artist;
  vm: {
    spotifyDisplay: string;
    youtubeDisplay: string;
    instagramDisplay: string;
    tiktokDisplay: string;
    youtubeShowClaim: boolean;
    instagramShowClaim: boolean;
    tiktokShowClaim: boolean;
    canOpenSpotify: boolean;
    canOpenYoutube: boolean;
    canOpenInstagram: boolean;
    canOpenTiktok: boolean;
    mobileMetricValue: string;
    mobileMetricHasLink: boolean;
  };
}

import { ButtonComponent } from '../../../components/button/button.component';
import { DropdownComponent, DropdownOption } from '../../../components/dropdown/dropdown.component';
import { KpiCardComponent } from '../../../components/kpi-card/kpi-card.component';
import { DateRangePickerComponent, DateRangeSelection } from '../../../components/date-range-picker/date-range-picker.component';
import { BadgeComponent } from '../../../components/badge/badge.component';
import {
  AudienceFilter,
  CareerFilter,
} from '../../../components/artist-filter/artist-filter.component';
import { SearchBarComponent } from '../../../components/search-bar/search-bar.component';
import { ChartTableComponent } from '../../../components/chart-table/chart-table.component';
import { ChartColumnDef } from '../../../components/chart-table/chart-table.types';
import { TableCustomizerComponent } from '../../../components/table-customizer/table-customizer.component';
import { TableKpiCardItem } from '../../../components/table-customizer/table-customizer.types';

@Component({
  selector: 'app-artist-rankings',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ArtistProfilePreviewDrawerComponent,
    ButtonComponent,
    DropdownComponent,
    KpiCardComponent,
    DateRangePickerComponent,
    BadgeComponent,
    SearchBarComponent,
    ChartTableComponent,
    TableCustomizerComponent,
    DashboardTickerComponent,
    SignInModalComponent,
  ],
  templateUrl: './artist-rankings.component.html',
})
export class ArtistRankingsComponent implements OnInit, OnDestroy {
  artistPreview = viewChild(ArtistProfilePreviewDrawerComponent);

  readonly layoutService = inject(LayoutService);
  /** Router listener for `/` ↔ `/artists` reuse (no component destroy). */
  private listingNavSubscription?: Subscription;
  /** Same-route “Overview” resets (filters / sort / drawer). */
  private overviewResetSubscription?: Subscription;
  /** Tracks URL path (no query) for router reuse between `/` and `/artists`. */
  private lastNormalizedUrl = '';

  openArtistDrawer(artist: Artist, event?: Event): void {
    event?.stopPropagation();
    const drawer = this.artistPreview();
    if (!drawer) return;
    this.layoutService.isSidebarOpen.set(true);
    drawer.open(artist);
  }

  private artistsService = inject(ArtistsService);
  private authService = inject(AuthService);
  private seoService = inject(SeoService);
  private suggestionService = inject(ArtistSuggestionService);
  private suggestArtistFlow = inject(SuggestArtistFlowService);
  private artistListingsUi = inject(ArtistListingsUiService);
  private overviewService = inject(OverviewService);
  private viewsService = inject(ViewsService);
  readonly dashboardTickerService = inject(DashboardTickerService);
  readonly isAuthenticated = this.authService.isAuthenticated;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);

  dbArtists = this.artistsService.artists;
  artists = signal<Artist[]>([]);
  loading = this.artistsService.loading;
  lastSync = this.artistsService.lastSync;
  isAdmin = this.authService.isAdmin;
  readonly defaultArtistPlaceholder = DEFAULT_ARTIST_IMAGE;
  protected readonly Math = Math;

  selectedGenres = signal<string[]>([]);
  filterMode = signal<FilterMode>('OR');
  searchQuery = signal<string>('');
  selectedAudienceFilters = signal<AudienceFilter[]>([]);
  selectedCareerFilters = signal<CareerFilter[]>([]);

  readonly audienceOptions: DropdownOption[] = [
    { id: 'rising', label: 'Rising', description: 'under 500K monthly listeners', value: 'rising' },
    { id: 'established', label: 'Established', description: '500K - 1M monthly listeners', value: 'established' },
    { id: 'household', label: 'Household name', description: 'over 1M monthly listeners', value: 'household' },
  ];

  readonly careerOptions: DropdownOption[] = [
    { id: 'newcomer', label: 'Newcomer', description: 'first release in the last 8 years', value: 'newcomer' },
    { id: 'building', label: 'Building', description: 'active 8 - 20 years', value: 'building' },
    { id: 'veteran', label: 'Veteran', description: 'active over 20 years', value: 'veteran' },
  ];

  activeFilterDropdown = signal<'audience' | 'career' | null>(null);


  hasActiveFilters = computed(() => {
    return (
      this.selectedAudienceFilters().length > 0 ||
      this.selectedCareerFilters().length > 0 ||
      this.searchQuery().trim().length > 0
    );
  });

  currentPage = signal<number>(1);
  pageSize = signal<number>(50);
  dropdownOpen = signal<boolean>(false);
  sortOption = signal<ArtistSortOption>('rank');
  sortDirection = signal<'asc' | 'desc'>('asc');
  sortDropdownOpen = signal(false);
  filterTrend = signal<'all' | 'up' | 'down' | 'breakout'>('all');
  mobileTooltip = signal<'top-gainer' | 'market-breadth' | 'breakouts' | 'releases' | null>(null);

  filteredArtistsCount = computed(() => {
    const trend = this.filterTrend();
    return this.activeArtists().filter((artist) => {
      if (trend === 'up') return this.hasPositiveTrend(artist);
      if (trend === 'down') return this.hasNegativeTrend(artist);
      if (trend === 'breakout') return this.isBreakoutArtist(artist);
      return true;
    }).length;
  });

  totalPages = computed(() => {
    const count = this.filteredArtistsCount();
    return Math.max(1, Math.ceil(count / this.pageSize()));
  });

  pageStartItem = computed(() => {
    const total = this.filteredArtistsCount();
    if (total === 0) return 0;
    const page = Math.min(Math.max(1, this.currentPage()), this.totalPages());
    return (page - 1) * this.pageSize() + 1;
  });

  pageEndItem = computed(() => {
    const total = this.filteredArtistsCount();
    const page = Math.min(Math.max(1, this.currentPage()), this.totalPages());
    return Math.min(page * this.pageSize(), total);
  });

  breadthTimeframe = signal<'24h' | '7d' | '15d' | '30d'>('7d');
  breadthDropdownOpen = signal<boolean>(false);
  customDateRangeLabel = signal<string | null>(null);
  selectedDatePreset = signal<ArtistDatePreset | null>(null);

  dateRangeOptions: DropdownOption[] = [
    { id: '24h', label: 'Last 24 hours', value: '24h' },
    { id: '7d', label: 'Last 7 days', value: '7d' },
    { id: '15d', label: 'Last 15 days', value: '15d' },
    { id: '30d', label: 'Last 30 days', value: '30d' },
  ];

  breadthTimeframeDisplayLabel = computed(() => {
    if (this.customDateRangeLabel()) {
      return this.customDateRangeLabel()!;
    }
    switch (this.breadthTimeframe()) {
      case '24h': return 'Last 24 hours';
      case '7d': return 'Last 7 days';
      case '15d': return 'Last 15 days';
      case '30d': return 'Last 30 days';
      default: return 'Last 24 hours';
    }
  });

  breadthTimeframeLabel = computed(() => {
    switch (this.breadthTimeframe()) {
      case '7d': return '7d';
      case '15d': return '15d';
      case '30d': return '30d';
      default: return '24h';
    }
  });

  breadthTimeframeTooltip = computed(() => {
    switch (this.breadthTimeframe()) {
      case '7d': return '7-day (1 week)';
      case '15d': return '15-day';
      case '30d': return '30-day (1 month)';
      default: return '24-hour';
    }
  });

  showSuggestionModal = signal<boolean>(false);
  suggestionForm = signal({
    name: '',
    spotifyUrl: '',
    youtubeUrl: '',
    instagramHandle: '',
    tiktokUrl: '',
    suggestedByEmail: '',
    additionalInfo: '',
  });
  suggestionSubmitting = signal<boolean>(false);
  suggestionSuccess = signal<boolean>(false);
  suggestionError = signal<string | null>(null);

  // --- Total Releases Popup Modal State ---
  showReleasesModal = signal<boolean>(false);
  selectedArtistForReleases = signal<Artist | null>(null);
  loadingReleases = signal<boolean>(false);
  releasesList = signal<ArtistRelease[]>([]);
  releasesTab = signal<'all' | 'solo' | 'featured'>('all');
  releasesSort = signal<'newest' | 'plays'>('newest');
  releasesSortDropdownOpen = signal<boolean>(false);

  releasesSortOptions: DropdownOption[] = [
    { id: 'newest', label: 'Sort by Newest', value: 'newest' },
    { id: 'plays', label: 'Sort by More plays', value: 'plays' },
  ];

  isReleaseSolo(r: ArtistRelease): boolean {
    if (r.isSolo !== undefined) {
      return r.isSolo;
    }
    if (r.artistCount !== undefined) {
      return r.artistCount === 1 && r.type !== 'appears_on';
    }
    return r.type !== 'appears_on';
  }

  releasesAll = computed(() => this.releasesList());
  releasesSolo = computed(() => this.releasesList().filter((r) => this.isReleaseSolo(r)));
  releasesFeatured = computed(() => this.releasesList().filter((r) => !this.isReleaseSolo(r)));

  currentTabReleases = computed(() => {
    const tab = this.releasesTab();
    const list = tab === 'solo' ? this.releasesSolo() : tab === 'featured' ? this.releasesFeatured() : this.releasesAll();
    const sort = this.releasesSort();
    return [...list].sort((a, b) => {
      if (sort === 'plays') {
        return (b.streams || 0) - (a.streams || 0);
      }
      const dateA = a.releaseDate || '';
      const dateB = b.releaseDate || '';
      return dateB.localeCompare(dateA);
    });
  });

  genres = computed(() => {
    const genreSet = new Set<string>();
    this.artists().forEach((artist) => {
      if (artist.genres && Array.isArray(artist.genres)) {
        artist.genres.forEach((genre) => genreSet.add(genre));
      }
    });
    return Array.from(genreSet).sort();
  });

  private enrichArtist(artist: Artist): Artist {
    const realSpotify = this.getMetricCurrentValue(artist, 'spotifyMonthlyListeners') || artist.spotifyMonthlyListeners || 0;
    const realYoutube = this.getMetricCurrentValue(artist, 'youtubeSubscribers') || artist.youtubeSubscribers || 0;
    const realInstagram = this.getMetricCurrentValue(artist, 'instagramFollowers') || artist.instagramFollowers || 0;
    const realTiktok = this.getMetricCurrentValue(artist, 'tiktokFollowers') || artist.tiktokFollowers || 0;

    const calcSpotify = Math.max(realSpotify, 1);
    const calcYoutube = Math.max(realYoutube, 1);
    const calcInstagram = Math.max(realInstagram, 1);
    const calcTiktok = Math.max(realTiktok, 1);

    const popularity = artist.popularity || 1;
    const spotifyChange = this.getMetricChangeValue(artist, 'spotifyMonthlyListeners');
    const youtubeChange = this.getMetricChangeValue(artist, 'youtubeSubscribers');

    const totalScore = (Math.log10(calcSpotify) + Math.log10(calcYoutube) + Math.log10(calcInstagram) + Math.log10(calcTiktok)/2) * (popularity*10);

    return {
      ...artist,
      id: artist.id,
      name: artist.name,
      slug: artist.slug,
      spotifyMonthlyListeners: realSpotify,
      youtubeSubscribers: realYoutube,
      instagramFollowers: realInstagram,
      tiktokFollowers: realTiktok,
      totalScore,
      metricsMap: {
        spotify: { monthlyListeners: realSpotify, change: spotifyChange },
        youtube: { subscribers: realYoutube, change: youtubeChange },
        instagram: { followers: realInstagram, change: 0 },
        tiktok: { followers: realTiktok, change: 0 },
      },
    } as Artist;
  }

  matchesArtistSearch(artist: Artist, rawQuery: string): boolean {
    const query = (rawQuery || '').trim();
    if (!query) return true;

    const qLower = query.toLowerCase();
    const qNoHash = qLower.startsWith('#') ? qLower.slice(1).trim() : '';

    // 1. Rank (with and without "#")
    const rankNum = artist.rank != null ? String(artist.rank) : '';
    const rankWithHash = artist.rank != null ? `#${artist.rank}` : '#-';
    if (rankNum && rankNum.toLowerCase().includes(qLower)) return true;
    if (rankWithHash.toLowerCase().includes(qLower)) return true;
    if (qNoHash && rankNum && rankNum.toLowerCase().includes(qNoHash)) return true;

    // 2. Artist name (strictly displayed name, no hidden aliases)
    if (artist.name && artist.name.toLowerCase().includes(qLower)) return true;

    // 3. Genre (strictly displayed genre subtitle)
    const displayedGenre = (artist.genres && artist.genres[0]) || '';
    if (displayedGenre && displayedGenre.toLowerCase().includes(qLower)) return true;

    // 4. Active since
    const activeSince = String(artist.activeSince || '2020');
    if (activeSince.toLowerCase().includes(qLower)) return true;

    // 5. Total songs
    const totalSongs = String(artist.totalSongs || 0);
    if (totalSongs.toLowerCase().includes(qLower)) return true;

    // 6. Avg track length
    const avgTrackLength = this.formatDuration(artist.avgTrackLength) || '03:15';
    if (avgTrackLength.toLowerCase().includes(qLower)) return true;

    // 7. IFPI rank (with and without "#")
    const ifpiVal = String(artist.chartedSongs || artist.rank || '-');
    const ifpiWithHash = `#${ifpiVal}`;
    if (ifpiVal.toLowerCase().includes(qLower)) return true;
    if (ifpiWithHash.toLowerCase().includes(qLower)) return true;
    if (qNoHash && ifpiVal.toLowerCase().includes(qNoHash)) return true;

    const matchesMetric = (display: string): boolean => {
      if (!display) return false;
      const dLower = display.toLowerCase();
      if (dLower.includes(qLower)) return true;
      const dNormalized = dLower.replace(/\.0([kmgtb])/g, '$1');
      const qNormalized = qLower.replace(/\.0([kmgtb])/g, '$1');
      return dNormalized.includes(qNormalized);
    };

    // 8. Spotify value
    const spotifyDisplay = this.getArtistMetricDisplay(artist, 'spotify');
    if (matchesMetric(spotifyDisplay)) return true;

    // 9. YouTube value
    const youtubeDisplay = this.getArtistPlatformCellDisplay(artist, 'youtube');
    if (matchesMetric(youtubeDisplay)) return true;

    // 10. Instagram value
    const instagramDisplay = this.getArtistPlatformCellDisplay(artist, 'instagram');
    if (matchesMetric(instagramDisplay)) return true;

    // 11. TikTok value
    const tiktokDisplay = this.getArtistPlatformCellDisplay(artist, 'tiktok');
    if (matchesMetric(tiktokDisplay)) return true;

    return false;
  }

  activeArtists = computed(() => {
    const audienceFilters = this.selectedAudienceFilters();
    const careerFilters = this.selectedCareerFilters();
    const currentYear = new Date().getFullYear();
    const query = this.searchQuery();

    return this.artists()
      .map((artist) => this.enrichArtist(artist))
      .filter((artist) => {
        let matchesGenre = true;
        if (this.selectedGenres().length > 0) {
          if (this.filterMode() === 'AND') {
            matchesGenre = this.selectedGenres().every(
              (genre) => artist.genres && artist.genres.includes(genre)
            );
          } else {
            matchesGenre = this.selectedGenres().some(
              (genre) => artist.genres && artist.genres.includes(genre)
            );
          }
        }

        const matchesSearch = this.matchesArtistSearch(artist, query);

        // Audience filter
        let matchesAudience = true;
        if (audienceFilters.length > 0) {
          const listeners = artist.spotifyMonthlyListeners || 0;
          matchesAudience = audienceFilters.some((f) => {
            if (f === 'rising') return listeners < 500000;
            if (f === 'established') return listeners >= 500000 && listeners <= 1000000;
            if (f === 'household') return listeners > 1000000;
            return false;
          });
        }

        // Career filter
        let matchesCareer = true;
        if (careerFilters.length > 0) {
          const yearStr = artist.activeSince;
          const parsedYear = yearStr ? parseInt(yearStr, 10) : NaN;
          const careerYears = isNaN(parsedYear) ? Math.max(0, currentYear - 2020) : Math.max(0, currentYear - parsedYear);

          matchesCareer = careerFilters.some((f) => {
            if (f === 'newcomer') return careerYears < 8;
            if (f === 'building') return careerYears >= 8 && careerYears <= 20;
            if (f === 'veteran') return careerYears > 20;
            return false;
          });
        }

        return matchesGenre && matchesSearch && matchesAudience && matchesCareer;
      })
      .sort((a, b) => (a.rank || 0) - (b.rank || 0));
  });

  topGainerArtist = computed(() => {
    const artists = this.activeArtists();
    const artistsWithGain = artists
      .map((artist) => ({
        artist,
        breakdown: this.getArtistCombinedGainBreakdown(artist),
      }))
      .filter((entry) => entry.breakdown.totalGain > 0)
      .sort((a, b) => b.breakdown.totalGain - a.breakdown.totalGain);

    return artistsWithGain[0]?.artist ?? artists[0] ?? null;
  });

  topGainerBreakdown = computed(() => {
    return this.getArtistCombinedGainBreakdown(this.topGainerArtist());
  });

  topGainerChangeLabel = computed(() => {
    const breakdown = this.topGainerBreakdown();
    if (breakdown.totalGain <= 0) return 'Stable';
    return `+${formatNumber(breakdown.totalGain)} gained`;
  });

  goneUpCount = computed(() => {
    return this.activeArtists().filter((artist) => this.hasPositiveTrend(artist)).length;
  });

  goneDownCount = computed(() => {
    return this.activeArtists().filter((artist) => this.hasNegativeTrend(artist)).length;
  });

  hottestGenre = computed(() => {
    return '-';
  });

  breakoutCount = computed(() => {
    return this.activeArtists().filter((artist) => {
      const listeners = this.getMetricCurrentValue(artist, 'spotifyMonthlyListeners') || artist.spotifyMonthlyListeners || 0;
      const growth = this.getMetricChangeValue(artist, 'spotifyMonthlyListeners');
      return listeners > 0 && listeners <= 100000 && growth > 0;
    }).length;
  });

  breakoutAvgGrowthDisplay = computed(() => {
    const tf = this.breadthTimeframe();
    const breakoutArtists = this.activeArtists().filter((artist) => {
      const growth = this.getMetricChangeValue(artist, 'spotifyMonthlyListeners');
      return growth > 0;
    });

    if (breakoutArtists.length === 0) return '+0%';

    const totalGrowthPct = breakoutArtists.reduce((sum, artist) => {
      const current = this.getMetricCurrentValue(artist, 'spotifyMonthlyListeners') || artist.spotifyMonthlyListeners || 10000;
      const change = this.getMetricChangeValue(artist, 'spotifyMonthlyListeners');
      const pct = (change / Math.max(1, current - change)) * 100;
      return sum + Math.max(0.5, Math.min(pct, 150));
    }, 0);

    const avgPct = Math.round(totalGrowthPct / breakoutArtists.length);
    const result = avgPct > 0 ? avgPct : (tf === '24h' ? 18 : tf === '7d' ? 24 : tf === '15d' ? 32 : 48);
    return `+ ${result}%`;
  });

  ifpiPercentageDisplay = computed(() => {
    const tf = this.breadthTimeframe();
    const active = this.activeArtists();
    const total = active.length;
    if (total === 0) return '0%';

    // An artist made it to IFPI charts ONLY if they have charted songs (chartedSongs > 0)
    const chartedArtists = active.filter((a) => (a.chartedSongs ?? 0) > 0);
    
    // Filter active charted artists in the selected timeframe window
    const activeChartedInTf = chartedArtists.filter((a) => {
      const change = this.getPrimaryMetricChange(a);
      if (tf === '24h') return change > 0;
      if (tf === '7d') return change !== 0;
      if (tf === '15d') return change !== 0;
      return true; // 30d: all artists with chartedSongs > 0
    });

    const pct = Math.round((activeChartedInTf.length / total) * 100);
    return `${pct}%`;
  });

  activeArtistsInTimeframe = computed(() => {
    const tf = this.breadthTimeframe();
    const artists = this.activeArtists();
    if (artists.length === 0) return 0;

    const days = tf === '24h' ? 1 : tf === '7d' ? 7 : tf === '15d' ? 15 : 30;
    const cutoffTime = Date.now() - days * 24 * 60 * 60 * 1000;

    return artists.filter((artist) => {
      const oldestRelease = (artist as any).oldestReleaseDate;
      const lastUpdated = (artist as any).lastUpdated;
      if (oldestRelease) {
        const t = new Date(oldestRelease).getTime();
        if (!isNaN(t) && t >= cutoffTime) return true;
      }
      if (lastUpdated) {
        const t = new Date(lastUpdated).getTime();
        if (!isNaN(t) && t >= cutoffTime) return true;
      }
      const change = this.getPrimaryMetricChange(artist);
      return change > 0;
    }).length;
  });

  kpiBreakoutCount = signal<number>(0);
  kpiBreakoutGrowth = signal<string>('+0%');
  kpiIfpiPercentage = signal<string>('0%');
  kpiActiveCount = signal<number>(0);
  kpisLoaded = signal<boolean>(false);

  breakoutGrowthCaption = computed(() => `${this.kpiBreakoutGrowth()} avg growth across platforms`);
  ifpiPercentageValue = computed(() => parseInt(this.kpiIfpiPercentage(), 10) || 0);

  isDateRangeLoading = signal<boolean>(false);
  private kpiRequestId = 0;
  private tickerRequestId = 0;
  private dateRangeRequestId = 0;
  selectedStartDate = signal<Date | null>(null);
  selectedEndDate = signal<Date | null>(null);

  async triggerKpiAnimation(): Promise<void> {
    const requestId = ++this.kpiRequestId;
    const tf = this.breadthTimeframe();
    const startDateStr = this.selectedStartDate() ? formatLocalDate(this.selectedStartDate()!) : undefined;
    const endDateStr = this.selectedEndDate() ? formatLocalDate(this.selectedEndDate()!) : undefined;
    const backendKpis = await this.artistsService.getOverviewKpis(tf, startDateStr, endDateStr);
    if (requestId !== this.kpiRequestId) return;

    this.kpiBreakoutCount.set(backendKpis?.breakoutCount ?? this.breakoutCount());
    this.kpiBreakoutGrowth.set(backendKpis?.breakoutAvgGrowth ?? this.breakoutAvgGrowthDisplay());
    this.kpiIfpiPercentage.set(backendKpis?.ifpiPercentage ?? this.ifpiPercentageDisplay());
    this.kpiActiveCount.set(backendKpis?.activeArtistsCount ?? this.activeArtistsInTimeframe());
    this.kpisLoaded.set(true);
  }

  releasesStats = computed(() => {
    const list = this.artists();
    if (!list.length) return { mean: 0, median: 0, mode: 0 };

    const songsCounts = list.map(a => a.totalSongs || 0).sort((a, b) => a - b);
    
    // Mean
    const sum = songsCounts.reduce((acc, val) => acc + val, 0);
    const mean = Math.round(sum / songsCounts.length);

    // Median
    const mid = Math.floor(songsCounts.length / 2);
    const median = songsCounts.length % 2 !== 0 
      ? songsCounts[mid] 
      : Math.round((songsCounts[mid - 1] + songsCounts[mid]) / 2);

    // Mode
    const counts: Record<number, number> = {};
    let maxCount = 0;
    let mode = 0;
    for (const val of songsCounts) {
      counts[val] = (counts[val] || 0) + 1;
      if (counts[val] > maxCount) {
        maxCount = counts[val];
        mode = val;
      }
    }

    return { mean, median, mode };
  });

  /** Pre-built class strings so the template does not call methods every change detection cycle. */
  readonly marketTrendUpButtonClass = computed(
    () =>
      'w-full flex items-center justify-between p-2 rounded-lg text-left ' + this.trendClassCore('up')
  );

  readonly marketTrendDownButtonClass = computed(
    () =>
      'w-full flex items-center justify-between p-2 rounded-lg text-left ' + this.trendClassCore('down')
  );

  readonly breakoutSpotlightCardClass = computed(() => {
    const base =
      'min-w-0 overflow-hidden [contain:paint] card-glow bg-card-data border border-card-data-border rounded-xl p-6 h-[170px] md:h-full w-full flex flex-col justify-between gap-3 md:gap-4 shadow-sm text-left cursor-pointer ';
    const active = this.filterTrend() === 'breakout';
    const tone = active ? 'ring-1 ring-inset ring-white/20 border-white/40' : '';
    const faded = this.kpiBreakoutCount() === 0 ? ' opacity-90' : '';
    return base + tone + faded;
  });

  readonly defaultColumns: ChartColumnDef<ArtistRankingsTableRow>[] = [
    {
      id: 'rank',
      header: 'GREECE',
      tooltip: 'Greek rank: position among Greek artists by total audience',
      tooltipPosition: 'bottom',
      type: 'custom',
      width: '80px',
      minWidth: '70px',
      sortable: true,
      immutable: true,
      visible: true,
      formatter: (val, row) => row.artist.rank || '-',
    },
    {
      id: 'globalRank',
      header: 'GLOBAL',
      tooltip: 'Global rank: position across all tracked artists by HypeTrack Score',
      tooltipPosition: 'bottom',
      type: 'custom',
      width: '80px',
      minWidth: '70px',
      sortable: true,
      immutable: true,
      visible: true,
      formatter: (val, row) => row.artist.globalRank || '-',
    },
    {
      id: 'name',
      header: 'Artist Name',
      type: 'custom',
      minWidth: '160px',
      sortable: true,
      immutable: true,
      visible: true,
    },
    {
      id: 'hypeScore',
      header: 'HypeTrack Score',
      tooltip: 'HypeTrack Score (0-100) across Spotify, YouTube, Instagram and TikTok, with its change over the last 7 days',
      tooltipPosition: 'bottom',
      type: 'custom',
      width: '190px',
      minWidth: '160px',
      sortable: true,
      visible: true,
      formatter: (val, row) => row.artist.hypeScore ?? '-',
    },
    {
      id: 'activeSince',
      header: 'Active Since',
      type: 'custom',
      align: 'center',
      width: '100px',
      minWidth: '85px',
      sortable: true,
      visible: true,
      formatter: (val, row) => row.artist.activeSince || '2020',
    },
    {
      id: 'totalSongs',
      header: 'Total Songs',
      type: 'custom',
      align: 'center',
      width: '95px',
      minWidth: '80px',
      sortable: true,
      visible: true,
    },
    {
      id: 'avgTrackLength',
      header: 'Avg Track Length',
      type: 'custom',
      align: 'center',
      width: '120px',
      minWidth: '100px',
      sortable: true,
      visible: true,
      formatter: (val, row) => this.formatDuration(row.artist.avgTrackLength) || '03:15',
    },
    {
      id: 'chartedSongs',
      header: 'IFPI Rank',
      tooltip: 'Tracks charted in IFPI official weekly charts',
      tooltipPosition: 'bottom',
      type: 'custom',
      align: 'center',
      width: '95px',
      minWidth: '85px',
      sortable: true,
      visible: true,
      formatter: (val, row) => row.artist.chartedSongs || row.artist.rank || '-',
    },
    {
      id: 'spotify',
      header: 'Spotify',
      tooltip: 'Spotify Monthly Listeners count',
      tooltipPosition: 'bottom',
      type: 'custom',
      align: 'center',
      width: '95px',
      minWidth: '80px',
      sortable: true,
      visible: true,
      formatter: (val, row) => row.vm.spotifyDisplay,
    },
    {
      id: 'youtube',
      header: 'YouTube',
      tooltip: 'YouTube Subscribers count',
      tooltipPosition: 'bottom',
      type: 'custom',
      align: 'center',
      width: '95px',
      minWidth: '80px',
      sortable: true,
      visible: true,
      formatter: (val, row) => row.vm.youtubeDisplay,
    },
    {
      id: 'instagram',
      header: 'Instagram',
      tooltip: 'Instagram Followers count',
      tooltipPosition: 'bottom',
      type: 'custom',
      align: 'center',
      width: '95px',
      minWidth: '80px',
      sortable: true,
      visible: true,
      formatter: (val, row) => row.vm.instagramDisplay,
    },
    {
      id: 'tiktok',
      header: 'TikTok',
      tooltip: 'TikTok Followers count',
      tooltipPosition: 'bottom',
      type: 'custom',
      align: 'center',
      width: '90px',
      minWidth: '75px',
      sortable: true,
      visible: true,
      formatter: (val, row) => row.vm.tiktokDisplay,
    },
  ];

  customizableColumns = signal<ChartColumnDef<ArtistRankingsTableRow>[]>(this.defaultColumns);

  displayedColumns = computed<ChartColumnDef<ArtistRankingsTableRow>[]>(() => {
    return this.customizableColumns().filter((col) => col.visible !== false);
  });

  get columns(): ChartColumnDef<ArtistRankingsTableRow>[] {
    return this.displayedColumns();
  }

  customizableKpis = signal<TableKpiCardItem[]>([
    { id: 'breakouts', label: 'Breakouts', visible: true },
    { id: 'ifpi', label: 'IFPI Charts', visible: true },
    { id: 'active', label: 'Most Active', visible: true },
  ]);

  isKpiVisible(id: string): boolean {
    return this.customizableKpis().find((k) => k.id === id)?.visible ?? true;
  }

  visibleKpiCount = computed(() => {
    return this.customizableKpis().filter((k) => k.visible).length;
  });

  visibleKpis = computed(() => {
    return this.customizableKpis().filter((k) => k.visible);
  });

  kpiGridClass = computed(() => {
    const count = this.visibleKpiCount();
    if (count === 3) return 'grid-cols-1 md:grid-cols-3';
    if (count === 2) return 'grid-cols-1 md:grid-cols-2';
    return 'grid-cols-1 max-w-sm';
  });

  isCustomizePanelOpen = signal(false);
  activeView = signal<SavedView | null>(null);
  views = signal<SavedView[]>([]);
  isSignInModalOpen = signal(false);
  pendingSaveDraftName = signal<string | null>(null);

  private readonly gridStateParamKeys = [
    'v', 'cols', 'hidden', 'kpis', 'aud', 'career', 'trend', 'q', 'sort', 'dir', 'size', 'page', 'tf', 'from', 'to', 'dp', VIEW_URL_PARAM,
  ];

  sharedView = signal<SharedView | null>(null);
  urlBaseView = computed<SharedView | null>(() => this.activeView() ?? this.sharedView());

  private get defaultUrlState(): ArtistGridUrlState {
    return {
      columns: this.defaultColumns.map((c) => c.id),
      hiddenColumns: [],
      hiddenKpis: [],
      audienceFilters: [],
      careerFilters: [],
      trend: 'all',
      search: '',
      sortOption: 'rank',
      sortDirection: 'asc',
      pageSize: 50,
      currentPage: 1,
      breadthTimeframe: '7d',
      startDate: null,
      endDate: null,
      datePreset: null,
    };
  }

  currentUrlState = computed<ArtistGridUrlState>(() => {
    const cols = this.customizableColumns();
    const start = this.selectedStartDate();
    const end = this.selectedEndDate();
    const preset = this.selectedDatePreset();
    return {
      columns: cols.map((c) => c.id),
      hiddenColumns: cols.filter((c) => c.visible === false).map((c) => c.id),
      hiddenKpis: this.customizableKpis().filter((k) => !k.visible).map((k) => k.id),
      audienceFilters: this.selectedAudienceFilters(),
      careerFilters: this.selectedCareerFilters(),
      trend: this.filterTrend(),
      search: this.searchQuery(),
      sortOption: this.sortOption(),
      sortDirection: this.sortDirection(),
      pageSize: this.pageSize(),
      currentPage: this.currentPage(),
      breadthTimeframe: this.breadthTimeframe(),
      startDate: !preset && start ? formatLocalDate(start) : null,
      endDate: !preset && end ? formatLocalDate(end) : null,
      datePreset: preset,
    };
  });

  isLayoutDirty = computed(() => {
    const view = this.activeView();
    const baseline = view ? this.completeViewConfig(view.config) : this.defaultUrlState;
    return !isArtistGridStateEqual(this.currentUrlState(), baseline);
  });

  private completeViewConfig(config: ArtistGridUrlState): ArtistGridUrlState {
    const columns = this.buildColumnsFromState(config);
    return {
      ...config,
      columns: columns.map((c) => c.id),
      hiddenColumns: columns.filter((c) => c.visible === false).map((c) => c.id),
    };
  }

  private buildColumnsFromState(state: ArtistGridUrlState): ChartColumnDef<ArtistRankingsTableRow>[] {
    const byId = new Map(this.defaultColumns.map((c) => [c.id, c]));
    const hiddenSet = new Set(state.hiddenColumns);
    const ordered = state.columns.filter((id) => byId.has(id));
    const defaultIds = this.defaultColumns.map((c) => c.id);
    defaultIds.forEach((id, index) => {
      if (ordered.includes(id)) return;
      const previousId = defaultIds.slice(0, index).reverse().find((prev) => ordered.includes(prev));
      ordered.splice(previousId ? ordered.indexOf(previousId) + 1 : 0, 0, id);
    });

    return ordered
      .map((id) => byId.get(id))
      .filter((col): col is ChartColumnDef<ArtistRankingsTableRow> => !!col)
      .map((col) => ({ ...col, visible: col.immutable ? true : !hiddenSet.has(col.id) }));
  }

  private buildKpisFromState(state: ArtistGridUrlState): TableKpiCardItem[] {
    const hiddenSet = new Set(state.hiddenKpis);
    return [
      { id: 'breakouts', label: 'Breakouts' },
      { id: 'ifpi', label: 'IFPI Charts' },
      { id: 'active', label: 'Most Active' },
    ].map((k) => ({ ...k, visible: !hiddenSet.has(k.id) }));
  }

  private applyState(state: ArtistGridUrlState): void {
    this.customizableColumns.set(this.buildColumnsFromState(state));
    this.customizableKpis.set(this.buildKpisFromState(state));
    this.selectedAudienceFilters.set(state.audienceFilters);
    this.selectedCareerFilters.set(state.careerFilters);
    this.filterTrend.set(state.trend);
    this.searchQuery.set(state.search);
    this.sortOption.set(state.sortOption);
    this.sortDirection.set(state.sortDirection);
    this.pageSize.set(state.pageSize);
    this.currentPage.set(
      this.artists().length > 0 ? Math.max(1, Math.min(state.currentPage, this.totalPages())) : state.currentPage
    );
    this.applyPeriod(state);
    this.hasAppliedInitialUrlState = true;
  }

  private applyPeriod(state: ArtistGridUrlState): void {
    if (state.datePreset === 'this_month') {
      const { startDate, endDate } = thisMonthRange();
      this.selectedDatePreset.set('this_month');
      this.selectedStartDate.set(startDate);
      this.selectedEndDate.set(endDate);
      this.customDateRangeLabel.set('This month');
      this.breadthTimeframe.set(timeframeForRange(startDate, endDate));
      return;
    }

    const start = state.startDate ? parseLocalDate(state.startDate) : null;
    const end = state.endDate ? parseLocalDate(state.endDate) : null;
    const hasRange = !!start && !!end;
    this.selectedDatePreset.set(null);
    this.selectedStartDate.set(hasRange ? start : null);
    this.selectedEndDate.set(hasRange ? end : null);
    this.customDateRangeLabel.set(hasRange ? this.formatRangeLabel(start!, end!) : null);
    this.breadthTimeframe.set(hasRange ? timeframeForRange(start!, end!) : state.breadthTimeframe);
  }

  private formatRangeLabel(start: Date, end: Date): string {
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return start.getTime() === end.getTime() ? fmt(start) : `${fmt(start)} - ${fmt(end)}`;
  }

  private periodKey(): string {
    const start = this.selectedStartDate();
    const end = this.selectedEndDate();
    return [
      this.selectedDatePreset() ?? '',
      this.breadthTimeframe(),
      start ? formatLocalDate(start) : '',
      end ? formatLocalDate(end) : '',
    ].join('|');
  }

  private async refreshPeriodData(): Promise<void> {
    const requestId = ++this.dateRangeRequestId;
    this.isDateRangeLoading.set(true);
    this.refreshTicker();
    try {
      const startTime = Date.now();
      await this.triggerKpiAnimation();
      const elapsed = Date.now() - startTime;
      const minDuration = 400;
      if (elapsed < minDuration) {
        await new Promise((res) => setTimeout(res, minDuration - elapsed));
      }
    } finally {
      if (requestId === this.dateRangeRequestId) {
        this.isDateRangeLoading.set(false);
      }
    }
  }

  private pickerRange = computed<{ startDate: Date; endDate: Date } | null>(() => {
    if (this.selectedDatePreset()) return null;
    const start = this.selectedStartDate();
    const end = this.selectedEndDate();
    if (start && end) return { startDate: start, endDate: end };
    const tf = this.breadthTimeframe();
    if (tf === '7d' || tf === '30d') return null;
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (tf === '15d' ? 14 : 0));
    return { startDate, endDate };
  });

  pickerPresetId = computed<string | null>(() => {
    const preset = this.selectedDatePreset();
    if (preset) return preset;
    if (this.pickerRange()) return null;
    return this.breadthTimeframe();
  });

  readonly periodOptions: TablePeriodOption[] = [
    { id: '7d', label: '7 days' },
    { id: '30d', label: '30 days' },
    { id: 'this_month', label: 'This month' },
  ];

  onPeriodSelect(id: string): void {
    const option = this.periodOptions.find((o) => o.id === id);
    if (!option) return;
    const range = id === 'this_month' ? thisMonthRange() : { startDate: null, endDate: null };
    void this.onDateRangeChange({ ...range, presetId: id, label: option.label });
  }

  pickerStartDate = computed(() => this.pickerRange()?.startDate ?? null);
  pickerEndDate = computed(() => this.pickerRange()?.endDate ?? null);

  private async applyUrlState(params: Params): Promise<void> {
    const viewId = parseViewUrlParam(params[VIEW_URL_PARAM]);
    const view = viewId !== null ? await this.fetchSharedView(viewId) : null;
    this.sharedView.set(view);
    this.applyState(decodeArtistGridState(params, view ? this.completeViewConfig(view.config) : this.defaultUrlState));
  }

  private async fetchSharedView(id: number): Promise<SharedView | null> {
    try {
      return await firstValueFrom(this.viewsService.getPublic(id));
    } catch {
      return null;
    }
  }

  private navigateToState(params: Params): void {
    const current = this.route.snapshot.queryParams;
    const preserved: Params = {};
    for (const [key, value] of Object.entries(current)) {
      if (!this.gridStateParamKeys.includes(key)) preserved[key] = value;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ...preserved, ...params },
      queryParamsHandling: '',
      replaceUrl: true,
    });
  }

  private async loadSavedViews(): Promise<void> {
    if (!this.isAuthenticated()) {
      this.views.set([]);
      return;
    }
    try {
      const list = await firstValueFrom(this.viewsService.list());
      this.views.set(list);
      if (!this.activeView() && !this.sharedView() && parseViewUrlParam(this.route.snapshot.queryParams[VIEW_URL_PARAM]) === null) {
        const current = this.currentUrlState();
        const match = list.find((v) => isArtistGridStateEqual(current, this.completeViewConfig(v.config)));
        if (match) this.activeView.set(match);
      }
    } catch (err) {
      console.error('Failed to load saved views:', err);
    }
  }

  onSelectView(view: SavedView | null): void {
    this.activeView.set(view);
    this.sharedView.set(null);
    this.applyStateAndRefresh(view ? view.config : this.defaultUrlState);
  }

  onResetLayout(): void {
    this.applyStateAndRefresh(this.urlBaseView()?.config ?? this.defaultUrlState);
  }

  private applyStateAndRefresh(state: ArtistGridUrlState): void {
    const periodBefore = this.periodKey();
    this.applyState(state);
    if (this.periodKey() !== periodBefore) {
      void this.refreshPeriodData();
    }
  }

  async onSaveView(payload: SaveViewPayload): Promise<void> {
    const config = this.currentUrlState();
    try {
      if (payload.mode === 'create') {
        const created = await firstValueFrom(this.viewsService.create(payload.name, config));
        this.views.update((list) => [...list, created]);
        this.activeView.set(created);
        this.sharedView.set(null);
      } else {
        const updated = await firstValueFrom(this.viewsService.update(payload.id, { config }));
        this.views.update((list) => list.map((v) => (v.id === updated.id ? updated : v)));
        this.activeView.set(updated);
      }
    } catch (err) {
      if (this.isDuplicateNameError(err) && payload.mode === 'create') {
        await this.loadSavedViews();
        this.pendingSaveDraftName.set(payload.name);
        return;
      }
      console.error('Failed to save view:', err);
    }
  }

  async onRenameView(payload: { id: number; name: string }): Promise<void> {
    try {
      const updated = await firstValueFrom(this.viewsService.update(payload.id, { name: payload.name }));
      this.views.update((list) => list.map((v) => (v.id === updated.id ? updated : v)));
      if (this.activeView()?.id === updated.id) this.activeView.set(updated);
    } catch (err) {
      if (this.isDuplicateNameError(err)) {
        await this.loadSavedViews();
        return;
      }
      console.error('Failed to rename view:', err);
    }
  }

  private isDuplicateNameError(err: unknown): boolean {
    return err instanceof HttpErrorResponse && err.status === 409;
  }

  async onDeleteView(id: number): Promise<void> {
    try {
      await firstValueFrom(this.viewsService.remove(id));
      this.views.update((list) => list.filter((v) => v.id !== id));
      if (this.activeView()?.id === id) {
        this.onSelectView(null);
      }
    } catch (err) {
      console.error('Failed to delete view:', err);
    }
  }

  onSaveRequiresAuth(draftName: string): void {
    this.isCustomizePanelOpen.set(false);
    this.pendingSaveDraftName.set(draftName);
    this.isSignInModalOpen.set(true);
  }

  async onSignInSuccess(): Promise<void> {
    this.isSignInModalOpen.set(false);
    await this.loadSavedViews();
    this.pendingSaveDraftName.set(suggestViewName(this.views()));
    this.isCustomizePanelOpen.set(true);
  }

  onSignInModalClosed(): void {
    this.isSignInModalOpen.set(false);
    this.pendingSaveDraftName.set(null);
  }

  private readonly loadViewsOnAuthEffect = effect(() => {
    const authenticated = this.isAuthenticated();
    untracked(() => {
      if (authenticated) {
        void this.loadSavedViews();
      } else {
        this.views.set([]);
        const active = this.activeView();
        if (active) this.sharedView.set(active);
        this.activeView.set(null);
      }
    });
  });

  private readonly ownSharedViewEffect = effect(() => {
    const shared = this.sharedView();
    const owned = shared ? this.views().find((v) => v.id === shared.id) : undefined;
    if (!owned) return;
    untracked(() => {
      this.activeView.set(owned);
      this.sharedView.set(null);
    });
  });

  private hasAppliedInitialUrlState = false;

  private readonly syncUrlEffect = effect(() => {
    const state = this.currentUrlState();
    const view = this.urlBaseView();
    if (!this.hasAppliedInitialUrlState) return;
    const encoded = encodeArtistGridState(state, view ? this.completeViewConfig(view.config) : this.defaultUrlState);
    const target: Params = view ? { [VIEW_URL_PARAM]: viewUrlSlug(view), ...encoded } : encoded;
    const current = this.route.snapshot.queryParams;
    const gridKeys = this.gridStateParamKeys.filter((key) => current[key] !== undefined || target[key] !== undefined);
    if (gridKeys.every((key) => String(current[key] ?? '') === String(target[key] ?? ''))) return;
    this.navigateToState(target);
  });

  sortedFilteredArtists = computed<ArtistRankingsTableRow[]>(() => {
    const trend = this.filterTrend();
    const baseArtists = this.activeArtists().filter((artist) => {
      if (trend === 'up') return this.hasPositiveTrend(artist);
      if (trend === 'down') return this.hasNegativeTrend(artist);
      if (trend === 'breakout') return this.isBreakoutArtist(artist);
      return true;
    });

    const sorted = [...baseArtists].sort((a, b) => {
      const option = this.sortOption();
      const direction = this.sortDirection();
      const sortFactor = direction === 'asc' ? 1 : -1;

      if (option === 'name') {
        return a.name.localeCompare(b.name) * sortFactor;
      }

      if (option === 'rank') {
        const rankA = a.rank ?? Number.POSITIVE_INFINITY;
        const rankB = b.rank ?? Number.POSITIVE_INFINITY;
        const aMissing = !Number.isFinite(rankA);
        const bMissing = !Number.isFinite(rankB);
        if (aMissing && !bMissing) return 1;
        if (!aMissing && bMissing) return -1;
        if (aMissing && bMissing) return 0;
        return (rankA - rankB) * sortFactor;
      }

      if (option === 'globalRank') {
        const rankA = a.globalRank || Number.POSITIVE_INFINITY;
        const rankB = b.globalRank || Number.POSITIVE_INFINITY;
        if (rankA === rankB) return 0;
        return (rankA - rankB) * sortFactor;
      }

      if (option === 'hypeScore') {
        return ((a.hypeScore ?? 0) - (b.hypeScore ?? 0)) * sortFactor;
      }

      if (option === 'totalSongs') {
        const valA = a.totalSongs ?? 0;
        const valB = b.totalSongs ?? 0;
        return (valA - valB) * sortFactor;
      }

      if (option === 'activeSince') {
        const valA = a.activeSince ?? '';
        const valB = b.activeSince ?? '';
        if (!valA && valB) return 1;
        if (valA && !valB) return -1;
        if (!valA && !valB) return 0;
        return valA.localeCompare(valB) * sortFactor;
      }

      if (option === 'avgTrackLength') {
        const valA = a.avgTrackLength ?? 0;
        const valB = b.avgTrackLength ?? 0;
        return (valA - valB) * sortFactor;
      }

      if (option === 'chartedSongs') {
        const valA = a.chartedSongs ?? 0;
        const valB = b.chartedSongs ?? 0;
        return (valA - valB) * sortFactor;
      }

      if (option === 'instagram') {
        const valueA = this.getMetricCurrentValue(a, 'instagramFollowers') || a.instagramFollowers || 0;
        const valueB = this.getMetricCurrentValue(b, 'instagramFollowers') || b.instagramFollowers || 0;
        const aMissing = !valueA || valueA <= 0;
        const bMissing = !valueB || valueB <= 0;
        if (aMissing && !bMissing) return 1;
        if (!aMissing && bMissing) return -1;
        if (aMissing && bMissing) return 0;
        return direction === 'asc' ? valueA - valueB : valueB - valueA;
      }

      const metricValue = (artist: Artist): number => {
        if (option === 'spotify') {
          return this.getMetricCurrentValue(artist, 'spotifyMonthlyListeners') || artist.spotifyMonthlyListeners || 0;
        }
        if (option === 'youtube') {
          return this.getMetricCurrentValue(artist, 'youtubeSubscribers') || artist.youtubeSubscribers || 0;
        }
        return this.getMetricCurrentValue(artist, 'tiktokFollowers') || artist.tiktokFollowers || 0;
      };

      const valueA = metricValue(a);
      const valueB = metricValue(b);

      const aMissing = !valueA || valueA <= 0;
      const bMissing = !valueB || valueB <= 0;
      if (aMissing && !bMissing) return 1;
      if (!aMissing && bMissing) return -1;
      if (aMissing && bMissing) return 0;

      return direction === 'asc' ? valueA - valueB : valueB - valueA;
    });

    return sorted.map((artist) => this.buildRankingTableRow(artist));
  });

  private buildRankingTableRow(artist: Artist): ArtistRankingsTableRow {
    const rowArtist: Artist = { ...artist };
    const youtubeDisplay = this.getArtistPlatformCellDisplay(rowArtist, 'youtube');
    const instagramDisplay = this.getArtistPlatformCellDisplay(rowArtist, 'instagram');
    const tiktokDisplay = this.getArtistPlatformCellDisplay(rowArtist, 'tiktok');
    return {
      artist: rowArtist,
      vm: {
        spotifyDisplay: this.getArtistMetricDisplay(rowArtist, 'spotify'),
        youtubeDisplay,
        instagramDisplay,
        tiktokDisplay,
        youtubeShowClaim: youtubeDisplay === '—',
        instagramShowClaim: instagramDisplay === '—',
        tiktokShowClaim: tiktokDisplay === '—',
        canOpenSpotify: this.canOpenArtistSpotify(rowArtist),
        canOpenYoutube: this.canOpenArtistPlatform(rowArtist, 'youtube'),
        canOpenInstagram: this.canOpenArtistPlatform(rowArtist, 'instagram'),
        canOpenTiktok: this.canOpenArtistPlatform(rowArtist, 'tiktok'),
        mobileMetricValue: this.mobileMetricValue(rowArtist),
        mobileMetricHasLink: this.mobileMetricHasLink(rowArtist),
      },
    };
  }

  readonly formatScoreChange = formatScoreChange;
  readonly scoreChangeClass = scoreChangeClass;

  getCellDisplay(row: ArtistRankingsTableRow, col: ChartColumnDef<ArtistRankingsTableRow>): string {
    if (col.formatter) {
      return String(col.formatter(null, row));
    }
    return '';
  }

  getHighlightParts(
    text: string | number | null | undefined,
    rawQuery: string,
    allowHashFallback = false
  ): { text: string; match: boolean }[] {
    const str = text != null ? String(text) : '';
    if (!str) return [];
    const query = (rawQuery || '').trim();
    if (!query) return [{ text: str, match: false }];

    const lowerStr = str.toLowerCase();
    let lowerQuery = query.toLowerCase();

    if (allowHashFallback && lowerQuery.startsWith('#') && !lowerStr.includes(lowerQuery)) {
      const withoutHash = lowerQuery.slice(1).trim();
      if (withoutHash && lowerStr.includes(withoutHash)) {
        lowerQuery = withoutHash;
      }
    }

    const matchLen = lowerQuery.length;
    if (matchLen === 0) return [{ text: str, match: false }];

    const parts: { text: string; match: boolean }[] = [];
    let startIndex = 0;

    while (startIndex < str.length) {
      const matchIndex = lowerStr.indexOf(lowerQuery, startIndex);
      if (matchIndex === -1) {
        parts.push({ text: str.slice(startIndex), match: false });
        break;
      }

      if (matchIndex > startIndex) {
        parts.push({ text: str.slice(startIndex, matchIndex), match: false });
      }

      parts.push({
        text: str.slice(matchIndex, matchIndex + matchLen),
        match: true,
      });

      startIndex = matchIndex + matchLen;
    }

    return parts;
  }

  hasMoreArtists = computed(() => {
    return this.currentPage() < this.totalPages();
  });

  remainingArtists = computed(() => {
    const total = this.filteredArtistsCount();
    const shown = this.pageEndItem();
    return Math.max(0, total - shown);
  });

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
      // Reserves space at the bottom of this page's own layout for the ticker bar below
      // (see artist-rankings.component.html), now that the bar is scoped to this page
      // instead of being toggled globally from the app shell.
      document.documentElement.style.setProperty('--bottom-bar-height', '45px');
    }
    this.lastNormalizedUrl = this.normalizeAppPath(this.router.url);
    this.overviewResetSubscription = this.artistListingsUi.reset$.subscribe(() => this.resetOverviewUiState());

    this.listingNavSubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const next = this.normalizeAppPath(e.urlAfterRedirects);
        if (next === this.lastNormalizedUrl) return;
        const prev = this.lastNormalizedUrl;
        this.lastNormalizedUrl = next;
        const isArtistListingPath = (p: string) => p === '/' || p === '/artists';
        if (isArtistListingPath(prev) && isArtistListingPath(next) && prev !== next) {
          this.artistPreview()?.closeSidebar();
          return;
        }
        if (isArtistListingPath(prev) && !isArtistListingPath(next)) {
          this.layoutService.isSidebarOpen.set(false);
          this.artistPreview()?.closeSidebar();
        }
      });

    this.seoService.updateTags({
      title: 'Top Greek Artists Rankings - HypeTrack',
      description: 'Browse the complete rankings...',
      url: 'https://www.hypetrack.gr/artists',
      type: 'website'
    });

    await this.applyUrlState(this.route.snapshot.queryParams);

    await this.artistsService.loadArtists();
    this.artists.set([...this.dbArtists()]);
    this.currentPage.set(Math.max(1, Math.min(this.currentPage(), this.totalPages())));
    this.triggerKpiAnimation();
    this.refreshTicker();

    this.route.queryParams.subscribe((params) => {
      if (params['suggest'] !== 'true') return;

      const hasContext = Boolean(
        params['artistName'] ||
          params['artistId'] ||
          params['platform'] ||
          params['spotifyUrl'] ||
          params['youtubeUrl'] ||
          params['instagramHandle'] ||
          params['tiktokUrl'] ||
          params['suggestedByEmail'] ||
          params['additionalInfo']
      );

      if (hasContext) {
        this.openSuggestionModal({
          name: (params['artistName'] as string) || '',
          spotifyUrl: (params['spotifyUrl'] as string) || '',
          youtubeUrl: (params['youtubeUrl'] as string) || '',
          instagramHandle: (params['instagramHandle'] as string) || '',
          tiktokUrl: (params['tiktokUrl'] as string) || '',
          suggestedByEmail: (params['suggestedByEmail'] as string) || '',
          additionalInfo: (params['additionalInfo'] as string) || '',
          artistId: (params['artistId'] as string) || undefined,
          platform: (params['platform'] as string) || undefined,
        });
      } else {
        this.openSuggestionModal();
      }

      setTimeout(() => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            suggest: null,
            t: null,
            artistName: null,
            artistId: null,
            platform: null,
            spotifyUrl: null,
            youtubeUrl: null,
            instagramHandle: null,
            tiktokUrl: null,
            suggestedByEmail: null,
            additionalInfo: null,
          },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }, 100);
    });
  }

  /**
   * Restores the Overview listing to its initial layout: default sort, no genre/search filters,
   * no market/breakout card filter, drawer closed, and scroll to top.
   */
  resetOverviewUiState(): void {
    this.selectedGenres.set([]);
    this.filterMode.set('OR');
    this.searchQuery.set('');
    this.selectedAudienceFilters.set([]);
    this.selectedCareerFilters.set([]);
    this.currentPage.set(1);
    this.dropdownOpen.set(false);
    this.sortOption.set('rank');
    this.sortDirection.set('asc');
    this.sortDropdownOpen.set(false);
    this.filterTrend.set('all');
    this.activeFilterDropdown.set(null);
    this.mobileTooltip.set(null);
    this.closeSuggestionModal();
    this.suggestionSubmitting.set(false);
    this.suggestionSuccess.set(false);
    this.suggestionError.set(null);
    this.layoutService.isSidebarOpen.set(false);
    this.artistPreview()?.closeSidebar();
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
  }

  onSearchChange(query: string): void {
    this.searchQuery.set(query);
    this.currentPage.set(1);
  }

  onAudienceFiltersChange(filters: AudienceFilter[]): void {
    this.selectedAudienceFilters.set(filters);
    this.currentPage.set(1);
  }

  onCareerFiltersChange(filters: CareerFilter[]): void {
    this.selectedCareerFilters.set(filters);
    this.currentPage.set(1);
  }

  onFilterDropdownOpenChange(dropdown: 'audience' | 'career', isOpen: boolean): void {
    if (isOpen) {
      this.activeFilterDropdown.set(dropdown);
    } else if (this.activeFilterDropdown() === dropdown) {
      this.activeFilterDropdown.set(null);
    }
  }

  onAudienceSelect(option: DropdownOption): void {
    if (option.value === 'all' || this.selectedAudienceFilters().includes(option.value)) {
      this.onAudienceFiltersChange([]);
    } else {
      this.onAudienceFiltersChange([option.value as AudienceFilter]);
    }
  }

  onCareerSelect(option: DropdownOption): void {
    if (option.value === 'all' || this.selectedCareerFilters().includes(option.value)) {
      this.onCareerFiltersChange([]);
    } else {
      this.onCareerFiltersChange([option.value as CareerFilter]);
    }
  }

  clearAllFilters(): void {
    this.onAudienceFiltersChange([]);
    this.onCareerFiltersChange([]);
    this.onSearchChange('');
  }

  handleGenreToggle(genre: string) {
    const current = this.selectedGenres();
    if (current.includes(genre)) {
      this.selectedGenres.set(current.filter((g) => g !== genre));
    } else {
      this.selectedGenres.set([...current, genre]);
    }
  }

  handleFilterModeChange(mode: FilterMode) {
    this.filterMode.set(mode);
  }

  handleSearchChange(query: string) {
    this.searchQuery.set(query);
    this.currentPage.set(1);
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.handleSearchChange(target.value);
  }

  clearGenres() {
    this.selectedGenres.set([]);
  }

  toggleDropdown() {
    this.dropdownOpen.set(!this.dropdownOpen());
  }

  toggleSortDropdown() {
    this.sortDropdownOpen.update(v => !v);
  }

  setSortOption(option: ArtistSortOption) {
    this.handleSort(option);
    this.sortDropdownOpen.set(false);
  }

  handleSort(option: ArtistSortOption) {
    if (this.sortOption() === option) {
      this.sortDirection.update(direction => direction === 'asc' ? 'desc' : 'asc');
      return;
    }

    this.sortOption.set(option);
    if (option === 'rank' || option === 'globalRank' || option === 'name') {
      this.sortDirection.set('asc');
    } else {
      this.sortDirection.set('desc');
    }
  }

  setTrendFilter(trend: 'all' | 'up' | 'down' | 'breakout'): void {
    if (trend === 'up' && this.goneUpCount() === 0) return;
    if (trend === 'down' && this.goneDownCount() === 0) return;
    if (trend === 'breakout' && this.kpiBreakoutCount() === 0) return;
    this.filterTrend.set(this.filterTrend() === trend ? 'all' : trend);
    this.currentPage.set(1);
  }

  toggleMobileTooltip(key: 'top-gainer' | 'market-breadth' | 'breakouts' | 'releases'): void {
    this.mobileTooltip.set(this.mobileTooltip() === key ? null : key);
  }

  isMobileTooltipOpen(key: 'top-gainer' | 'market-breadth' | 'breakouts' | 'releases'): boolean {
    return this.mobileTooltip() === key;
  }

  sortDisplayLabel(): string {
    const option = this.sortOption();
    if (option === 'rank') return this.isMobileViewport() ? 'Spotify' : 'Greece Rank';
    if (option === 'globalRank') return 'Global Rank';
    if (option === 'hypeScore') return 'HypeTrack Score';
    if (option === 'name') return 'Name';
    if (option === 'spotify') return 'Spotify';
    if (option === 'youtube') return 'YouTube';
    if (option === 'instagram') return 'Instagram';
    return 'TikTok';
  }

  private trendClassCore(trend: 'up' | 'down' | 'breakout'): string {
    const active = this.filterTrend() === trend;
    return active
      ? 'bg-slate-800 border border-white/40'
      : 'bg-glow border border-transparent hover:border-slate-700';
  }

  mobileMetricColumnLabel(): string {
    const option = this.sortOption();
    if (option === 'youtube') return 'YouTube';
    if (option === 'instagram') return 'Instagram';
    if (option === 'tiktok') return 'TikTok';
    return 'Spotify';
  }

  mobileMetricValue(artist: Artist): string {
    const option = this.sortOption();
    if (option === 'youtube') {
      const value = this.getArtistMetricDisplay(artist, 'youtube');
      return value;
    }
    if (option === 'instagram') {
      const value = this.getArtistMetricDisplay(artist, 'instagram');
      if (value !== '—') return value;
      const handle = this.getInstagramSortHandle(artist);
      return handle ? `@${handle}` : '—';
    }
    if (option === 'tiktok') {
      const value = this.getArtistMetricDisplay(artist, 'tiktok');
      if (value !== '—') return value;
      const handle = this.getTiktokSortHandle(artist);
      return handle ? `@${handle}` : '—';
    }
    return this.getArtistMetricDisplay(artist, 'spotify');
  }

  mobileMetricHasLink(artist: Artist): boolean {
    const option = this.sortOption();
    const platform: 'spotify' | 'youtube' | 'instagram' | 'tiktok' =
      option === 'youtube' || option === 'instagram' || option === 'tiktok' ? option : 'spotify';
    return !!this.getArtistPlatformUrl(artist, platform);
  }

  openMobileMetricLink(artist: Artist, event?: Event): void {
    event?.stopPropagation();
    const option = this.sortOption();
    const platform: 'spotify' | 'youtube' | 'instagram' | 'tiktok' =
      option === 'youtube' || option === 'instagram' || option === 'tiktok' ? option : 'spotify';
    const url = this.getArtistPlatformUrl(artist, platform);
    if (!url || !isPlatformBrowser(this.platformId)) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  getArtistMetricDisplay(artist: Artist, metric: 'spotify' | 'youtube' | 'instagram' | 'tiktok'): string {
    if (metric === 'instagram' && artist.instagramInvalid) return '—';
    const map = (artist as any).metricsMap;
    if (metric === 'spotify') return this.formatMetricValue(map?.spotify?.monthlyListeners ?? artist.spotifyMonthlyListeners ?? 0);
    if (metric === 'youtube') return this.formatMetricValue(map?.youtube?.subscribers ?? artist.youtubeSubscribers ?? 0);
    if (metric === 'instagram') return this.formatMetricValue(map?.instagram?.followers ?? (artist.instagramFollowers != null ? Number(artist.instagramFollowers) : 0));
    return this.formatMetricValue(map?.tiktok?.followers ?? artist.tiktokFollowers ?? 0);
  }

  getArtistPlatformCellDisplay(artist: Artist, platform: 'youtube' | 'instagram' | 'tiktok'): string {
    if (platform === 'instagram' && artist.instagramInvalid) {
      return '-';
    }

    const numericDisplay = this.getArtistMetricDisplay(artist, platform);
    if (numericDisplay !== '—') return numericDisplay;

    if (platform === 'instagram') {
      const ig = this.getInstagramSortHandle(artist);
      return ig ? `@${ig}` : '—';
    }

    if (platform === 'tiktok') {
      const tk = this.getTiktokSortHandle(artist);
      return tk ? `@${tk}` : '—';
    }

    return '—';
  }

  getRankChangeDisplay(artist: Artist): string {
    const change = artist.rankChange ?? 0;
    if (change > 0) return `+${change}`;
    if (change < 0) return `${change}`;
    return '0';
  }

  formatDuration(ms: number | null | undefined): string {
    if (!ms || ms <= 0) return '—';
    const totalSeconds = Math.round(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  private formatMetricValue(value: number): string {
    if (!value || value <= 0) return '—';
    return formatNumber(value);
  }

  toggleBreadthDropdown(event?: Event): void {
    event?.stopPropagation();
    this.breadthDropdownOpen.update(v => !v);
  }

  selectBreadthTimeframe(tf: '24h' | '7d' | '15d' | '30d'): void {
    this.breadthTimeframe.set(tf);
    this.breadthDropdownOpen.set(false);
    this.triggerKpiAnimation();
  }

  onDateRangeSelect(option: DropdownOption): void {
    if (option.value) {
      this.selectBreadthTimeframe(option.value as '24h' | '7d' | '15d' | '30d');
    }
  }

  async onDateRangeChange(selection: DateRangeSelection): Promise<void> {
    if (selection.presetId && selection.presetId !== 'this_month') {
      this.selectedDatePreset.set(null);
      this.selectedStartDate.set(null);
      this.selectedEndDate.set(null);
      this.customDateRangeLabel.set(null);
      let tf: '24h' | '7d' | '15d' | '30d' = '30d';
      if (selection.presetId === '7d') tf = '7d';
      else if (selection.presetId === '30d') tf = '30d';
      this.breadthTimeframe.set(tf);
    } else {
      this.selectedDatePreset.set(selection.presetId === 'this_month' ? 'this_month' : null);
      this.selectedStartDate.set(selection.startDate);
      this.selectedEndDate.set(selection.endDate);
      const label = selection.presetId === 'this_month' ? 'This month' : selection.label;
      this.customDateRangeLabel.set(label);

      if (selection.startDate && selection.endDate) {
        this.breadthTimeframe.set(timeframeForRange(selection.startDate, selection.endDate));
      }
    }

    await this.refreshPeriodData();
  }

  goToPage(page: number): void {
    const maxPage = this.totalPages();
    const target = Math.max(1, Math.min(page, maxPage));
    this.currentPage.set(target);
  }

  prevPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((p) => p + 1);
    }
  }

  onGoToInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = parseInt(input.value, 10);
    if (isNaN(val)) {
      input.value = this.currentPage().toString();
      return;
    }
    this.goToPage(val);
    input.value = this.currentPage().toString();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.breadthDropdownOpen()) {
      this.breadthDropdownOpen.set(false);
    }
  }

  private getPrimaryMetricChange(artist: Artist): number {
    const tf = this.breadthTimeframe();
    const metrics = artist.metrics ?? [];
    const spotifyMetric = metrics.find(m => m.name === 'spotifyMonthlyListeners') || metrics[0];
    if (spotifyMetric) {
      if (spotifyMetric.changes && spotifyMetric.changes[tf] !== undefined) {
        return spotifyMetric.changes[tf]!;
      }
      if (spotifyMetric.prev?.change !== undefined) {
        return spotifyMetric.prev.change;
      }
    }
    return (artist.rankChange ?? 0);
  }

  private hasPositiveTrend(artist: Artist): boolean {
    return this.getPrimaryMetricChange(artist) > 0;
  }

  private hasNegativeTrend(artist: Artist): boolean {
    return this.getPrimaryMetricChange(artist) < 0;
  }

  private isBreakoutArtist(artist: Artist): boolean {
    const listeners = this.getMetricCurrentValue(artist, 'spotifyMonthlyListeners') || artist.spotifyMonthlyListeners || 0;
    const growth = this.getMetricChangeValue(artist, 'spotifyMonthlyListeners');
    return listeners > 0 && listeners <= 100000 && growth > 0;
  }

  private isMobileViewport(): boolean {
    return isPlatformBrowser(this.platformId) ? window.innerWidth < 768 : false;
  }

  openSuggestionModal(
    preset?: Partial<{
      name: string;
      spotifyUrl: string;
      youtubeUrl: string;
      instagramHandle: string;
      tiktokUrl: string;
      suggestedByEmail: string;
      additionalInfo: string;
    }> & { artistId?: string; platform?: string }
  ): void {
    this.showSuggestionModal.set(true);
    this.suggestionSuccess.set(false);
    this.suggestionError.set(null);

    const empty = {
      name: '',
      spotifyUrl: '',
      youtubeUrl: '',
      instagramHandle: '',
      tiktokUrl: '',
      suggestedByEmail: '',
      additionalInfo: '',
    };

    if (!preset || !this.suggestionPresetHasData(preset)) {
      this.suggestionForm.set(empty);
      return;
    }

    let additionalInfo = (preset.additionalInfo ?? '').trim();
    if (preset.artistId?.trim()) {
      const line = `HypeTrack profile: /artist/${preset.artistId.trim()}`;
      additionalInfo = additionalInfo ? `${additionalInfo}\n${line}` : line;
    }
    if (preset.platform?.trim()) {
      const line = `Claim / verify: ${preset.platform.trim()}`;
      additionalInfo = additionalInfo ? `${additionalInfo}\n${line}` : line;
    }

    this.suggestionForm.set({
      name: (preset.name ?? '').trim(),
      spotifyUrl: (preset.spotifyUrl ?? '').trim(),
      youtubeUrl: (preset.youtubeUrl ?? '').trim(),
      instagramHandle: (preset.instagramHandle ?? '').trim(),
      tiktokUrl: (preset.tiktokUrl ?? '').trim(),
      suggestedByEmail: (preset.suggestedByEmail ?? '').trim(),
      additionalInfo,
    });
  }

  private suggestionPresetHasData(
    preset: Partial<{
      name: string;
      spotifyUrl: string;
      youtubeUrl: string;
      instagramHandle: string;
      tiktokUrl: string;
      suggestedByEmail: string;
      additionalInfo: string;
    }> & { artistId?: string; platform?: string }
  ): boolean {
    if (preset.artistId?.trim() || preset.platform?.trim()) return true;
    return ['name', 'spotifyUrl', 'youtubeUrl', 'instagramHandle', 'tiktokUrl', 'suggestedByEmail', 'additionalInfo'].some(
      (key) => {
        const v = preset[key as keyof typeof preset];
        return typeof v === 'string' && v.trim().length > 0;
      }
    );
  }

  closeSuggestionModal() {
    this.showSuggestionModal.set(false);
    this.suggestionForm.set({
      name: '',
      spotifyUrl: '',
      youtubeUrl: '',
      instagramHandle: '',
      tiktokUrl: '',
      suggestedByEmail: '',
      additionalInfo: '',
    });
  }

  async submitSuggestion() {
    const form = this.suggestionForm();
    if (!form.name.trim()) {
      this.suggestionError.set('Artist name is required');
      return;
    }

    const hasAtLeastOneSocialLink = Boolean(
      form.spotifyUrl.trim() ||
      form.youtubeUrl.trim() ||
      form.instagramHandle.trim() ||
      form.tiktokUrl.trim()
    );

    if (!hasAtLeastOneSocialLink) {
      this.suggestionError.set('Add at least one official link (Spotify, YouTube, Instagram, or TikTok)');
      return;
    }

    this.suggestionSubmitting.set(true);
    this.suggestionError.set(null);

    try {
      // @ts-ignore - Backend accepts tiktokUrl, frontend DTO is pending update.
      await this.suggestionService.create({
        name: form.name.trim(),
        spotifyUrl: form.spotifyUrl.trim() || undefined,
        youtubeUrl: form.youtubeUrl.trim() || undefined,
        instagramHandle: form.instagramHandle.trim() || undefined,
        // @ts-ignore - Backend accepts tiktokUrl, frontend DTO is pending update.
        tiktokUrl: form.tiktokUrl.trim() || undefined,
        suggestedByEmail: form.suggestedByEmail.trim() || undefined,
        additionalInfo: form.additionalInfo.trim() || undefined,
      }).toPromise();

      this.suggestionSuccess.set(true);
      setTimeout(() => {
        this.closeSuggestionModal();
      }, 2000);
    } catch (error: any) {
      this.suggestionError.set(error?.error?.message || 'Failed to submit suggestion');
    } finally {
      this.suggestionSubmitting.set(false);
    }
  }

  private getMetricCurrentValue(artist: Artist, metricName: string): number {
    // 1. Mirar en la raíz (por si David lo manda suelto)
    const rootValue = (artist as any)[metricName];
    if (rootValue) return rootValue;

    // 2. Mirar en el array metrics
    const metric = (artist.metrics ?? []).find((item) => item.name === metricName);
    return metric?.current?.value ?? 0;
  }

  private getMetricChangeValue(artist: Artist, metricName: string): number {
    const tf = this.breadthTimeframe();
    const metric = (artist.metrics ?? []).find((item) => item.name === metricName);
    if (metric) {
      if (metric.changes && metric.changes[tf] !== undefined) {
        return metric.changes[tf]!;
      }
      if (metric.prev?.change !== undefined) {
        const baseChange = metric.prev.change;
        const multiplier = tf === '7d' ? 3.5 : tf === '15d' ? 6.5 : tf === '30d' ? 12 : 1;
        return Math.round(baseChange * multiplier);
      }
    }
    return 0;
  }

  private getMetricPercentageChange(artist: Artist, metricName: string): number {
    const metric = (artist.metrics ?? []).find((item) => item.name === metricName);
    const absChange = metric?.prev?.change ?? 0;
    if (!absChange) return 0;
    const currentVal = this.getMetricCurrentValue(artist, metricName);
    const prevVal = metric?.prev?.value !== undefined ? metric.prev.value : (currentVal - absChange);
    if (prevVal > 0) {
      return (absChange / prevVal) * 100;
    }
    return 0;
  }

  getArtistCombinedGainBreakdown(artist: Artist | null): {
    spotify: number;
    youtube: number;
    instagram: number;
    tiktok: number;
    totalGain: number;
  } {
    if (!artist) return { spotify: 0, youtube: 0, instagram: 0, tiktok: 0, totalGain: 0 };
    const spotifyChange = this.getMetricChangeValue(artist, 'spotifyMonthlyListeners');
    const youtubeChange = this.getMetricChangeValue(artist, 'youtubeSubscribers');
    const instagramChange = this.getMetricChangeValue(artist, 'instagramFollowers');
    const tiktokChange = this.getMetricChangeValue(artist, 'tiktokFollowers');

    const totalGain =
      (spotifyChange > 0 ? spotifyChange : 0) +
      (youtubeChange > 0 ? youtubeChange : 0) +
      (instagramChange > 0 ? instagramChange : 0) +
      (tiktokChange > 0 ? tiktokChange : 0);

    return {
      spotify: spotifyChange,
      youtube: youtubeChange,
      instagram: instagramChange,
      tiktok: tiktokChange,
      totalGain,
    };
  }

  formatChangeValue(val: number): string {
    if (!val || val === 0) return '0';
    return val > 0 ? `+${val.toLocaleString()}` : `${val.toLocaleString()}`;
  }

  private getInstagramSortHandle(artist: Artist): string {
    const directHandle = String((artist as any).instagramHandle ?? '').trim();
    if (directHandle) {
      return directHandle.replace(/^@/, '').toLowerCase();
    }

    const socials = ((artist as any).socialMedia || (artist as any).social_media || (artist as any).socials || []) as any[];
    const instagram = socials.find((entry: any) => {
      const site = String(entry?.site ?? entry?.name ?? '').toLowerCase();
      return site === 'instagram';
    });

    if (!instagram) return '';

    const externalId = String(instagram.externalId ?? instagram.external_id ?? instagram.handle ?? instagram.username ?? '').trim();
    if (externalId) {
      return externalId.replace(/^@/, '').toLowerCase();
    }

    const url = String(instagram.url ?? instagram.link ?? '').trim();
    if (!url) return '';

    const lastSegment = url.split('/').filter(Boolean).pop() || '';
    return lastSegment.replace(/^@/, '').toLowerCase();
  }

  private getTiktokSortHandle(artist: Artist): string {
    const directHandle = String((artist as any).tiktokHandle ?? (artist as any).tiktok_handle ?? '').trim();
    if (directHandle) {
      return directHandle.replace(/^@/, '').toLowerCase();
    }

    const socials = ((artist as any).socialMedia || (artist as any).social_media || (artist as any).socials || []) as any[];
    const tiktok = socials.find((entry: any) => {
      const site = String(entry?.site ?? entry?.name ?? '').toLowerCase();
      return site === 'tiktok';
    });

    if (!tiktok) return '';

    const externalId = String(tiktok.externalId ?? tiktok.external_id ?? tiktok.handle ?? tiktok.username ?? '').trim();
    if (externalId) {
      return externalId.replace(/^@/, '').toLowerCase();
    }

    const url = String(tiktok.url ?? tiktok.link ?? '').trim();
    if (!url) return '';

    const lastSegment = url.split('/').filter(Boolean).pop() || '';
    return lastSegment.replace(/^@/, '').toLowerCase();
  }

  openClaimForArtist(artist: Artist, platform: 'spotify' | 'youtube' | 'instagram' | 'tiktok'): void {
    const routeParam = artist.slug || artist.id;
    if (!routeParam) return;
    this.suggestArtistFlow.open({
      artistName: artist.name,
      artistId: String(routeParam),
      platform,
    });
  }

  canOpenArtistSpotify(artist: Artist): boolean {
    return !!this.getArtistPlatformUrl(artist, 'spotify');
  }

  openArtistSpotify(artist: Artist, event?: Event): void {
    event?.stopPropagation();
    const url = this.getArtistPlatformUrl(artist, 'spotify');
    if (!url || !isPlatformBrowser(this.platformId)) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  canOpenArtistPlatform(artist: Artist, platform: 'youtube' | 'instagram' | 'tiktok'): boolean {
    return !!this.getArtistPlatformUrl(artist, platform);
  }

  openArtistPlatform(artist: Artist, platform: 'youtube' | 'instagram' | 'tiktok', event?: Event): void {
    event?.stopPropagation();
    const url = this.getArtistPlatformUrl(artist, platform);
    if (!url || !isPlatformBrowser(this.platformId)) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private getArtistPlatformUrl(artist: Artist, platform: 'spotify' | 'youtube' | 'instagram' | 'tiktok'): string | null {
    const item = artist as any;
    const normalizeHandle = (value: string): string => value.replace(/^@/, '').trim();
    const ensureHttp = (value: string): string => (/^https?:\/\//i.test(value) ? value : `https://${value}`);
    const socials = (item.socialMedia || item.social_media || item.socials || []) as any[];
    const social = socials.find((entry: any) => {
      const site = String(entry?.site ?? entry?.name ?? '').toLowerCase();
      return site === platform;
    });

    if (platform === 'spotify') {
      const spotifyUrl = String(item.spotifyUrl || item.spotify_url || social?.url || social?.link || '').trim();
      return spotifyUrl ? ensureHttp(spotifyUrl) : null;
    }
    if (platform === 'youtube') {
      const youtubeUrl = String(item.youtubeUrl || item.youtube_url || social?.url || social?.link || '').trim();
      if (youtubeUrl) return ensureHttp(youtubeUrl);
      const channelId = String(item.youtubeChannelId || item.youtube_channel_id || social?.externalId || social?.external_id || '').trim();
      return channelId ? `https://www.youtube.com/channel/${channelId}` : null;
    }
    if (platform === 'instagram') {
      if (item.instagramInvalid) return null;
      const instagramUrl = String(item.instagramUrl || item.instagram_url || social?.url || social?.link || '').trim();
      if (instagramUrl) return ensureHttp(instagramUrl);
      const handle = this.getInstagramSortHandle(artist);
      return handle ? `https://www.instagram.com/${normalizeHandle(handle)}` : null;
    }
    const tiktokUrl = String(item.tiktokUrl || item.tiktok_url || social?.url || social?.link || '').trim();
    if (tiktokUrl) return ensureHttp(tiktokUrl);
    const handle = String(item.tiktokHandle ?? item.tiktok_handle ?? '').trim();
    return handle ? `https://www.tiktok.com/@${normalizeHandle(handle)}` : null;
  }

  ngOnDestroy(): void {
    this.listingNavSubscription?.unsubscribe();
    this.listingNavSubscription = undefined;
    this.overviewResetSubscription?.unsubscribe();
    this.overviewResetSubscription = undefined;
    this.artistPreview()?.closeSidebar();
    this.layoutService.isSidebarOpen.set(false);
    this.dashboardTickerService.items.set([]);
    if (isPlatformBrowser(this.platformId)) {
      document.documentElement.style.setProperty('--bottom-bar-height', '0px');
    }
  }

  private async refreshTicker(): Promise<void> {
    const requestId = ++this.tickerRequestId;
    this.dashboardTickerService.loading.set(true);
    try {
      // breadthTimeframe already tracks the page's selected date range (preset or custom,
      // see onDateRangeChange) - reusing it here keeps the ticker in sync with it instead
      // of always showing a fixed 7-day delta regardless of what's selected above.
      // For a custom range ("This month"), pass the real calendar dates through instead of
      // the day-count approximation stored in breadthTimeframe, so the ticker reflects the
      // actual 1st-of-month-to-today window rather than whichever 7/15/30-day bucket it maps to.
      const startDateStr = this.selectedStartDate() ? formatLocalDate(this.selectedStartDate()!) : undefined;
      const endDateStr = this.selectedEndDate() ? formatLocalDate(this.selectedEndDate()!) : undefined;
      const items = await firstValueFrom(this.overviewService.getTicker(this.breadthTimeframe(), startDateStr, endDateStr));
      if (requestId !== this.tickerRequestId) return;
      this.dashboardTickerService.items.set(items);
    } catch (error) {
      if (requestId !== this.tickerRequestId) return;
      console.error('Failed to load dashboard ticker:', error);
      this.dashboardTickerService.items.set([]);
    } finally {
      if (requestId === this.tickerRequestId) {
        this.dashboardTickerService.loading.set(false);
      }
    }
  }

  private normalizeAppPath(url: string): string {
    const raw = url.split('#')[0].split('?')[0];
    if (raw === '') return '/';
    return raw.startsWith('/') ? raw : `/${raw}`;
  }

  async openReleasesModal(artist: Artist, event?: MouseEvent): Promise<void> {
    if (event) {
      event.stopPropagation();
    }
    this.selectedArtistForReleases.set(artist);
    this.releasesTab.set('all');
    this.releasesSort.set('newest');
    this.releasesSortDropdownOpen.set(false);
    this.showReleasesModal.set(true);
    this.loadingReleases.set(true);

    try {
      const releases = await this.artistsService.getArtistReleases(artist.id);
      this.releasesList.set(releases);
    } catch (err) {
      console.error('Error fetching artist discography:', err);
    } finally {
      this.loadingReleases.set(false);
    }
  }

  closeReleasesModal(): void {
    this.showReleasesModal.set(false);
    this.selectedArtistForReleases.set(null);
    this.releasesSortDropdownOpen.set(false);
  }

  selectReleasesTab(tab: 'all' | 'solo' | 'featured'): void {
    this.releasesTab.set(tab);
  }

  onReleasesSortSelect(option: DropdownOption): void {
    if (option.value) {
      this.releasesSort.set(option.value as 'newest' | 'plays');
    }
  }

  selectReleasesSort(sort: 'newest' | 'plays'): void {
    this.releasesSort.set(sort);
    this.releasesSortDropdownOpen.set(false);
  }

  viewArtistProfileFromModal(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    const artist = this.selectedArtistForReleases();
    this.closeReleasesModal();
    if (artist) {
      const slugOrId = artist.slug || artist.id;
      this.router.navigate(['/artist', slugOrId]);
    }
  }

  formatRelativeReleaseDate(dateStr: string | null): string {
    if (!dateStr) return 'Recently';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
}